import React, { useState, useEffect, useRef } from "react";
import {
  Box, Typography, Button, Link, CircularProgress,
  Alert, Fade, useMediaQuery,
} from "@mui/material";
import ShieldIcon from "@mui/icons-material/Shield";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTheme } from "@mui/material/styles";
import api from "../utils/api";
import { storeSession } from "../Services/loginService";

/* ── Individual OTP digit boxes ─────────────────────────────────────────── */
const OTPInput = ({ value, onChange, disabled }) => {
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const digits = Array.from({ length: 6 }, (_, i) => value[i] ?? "");
  const focusIdx = Math.min(value.length, 5);

  return (
    <Box
      sx={{ position: "relative", display: "flex", gap: { xs: 0.75, sm: 1 }, justifyContent: "center" }}
      onClick={() => inputRef.current?.focus()}
    >
      {digits.map((d, i) => (
        <Box
          key={i}
          sx={{
            width: { xs: 42, sm: 46 },
            height: { xs: 52, sm: 58 },
            borderRadius: "12px",
            border: "2px solid",
            borderColor:
              i === focusIdx && !disabled ? "#2f63ad"
              : d ? "rgba(47,99,173,0.35)"
              : "rgba(0,0,0,0.14)",
            backgroundColor: d ? "rgba(47,99,173,0.05)" : "rgba(255,255,255,0.97)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1.65rem",
            fontWeight: 700,
            fontFamily: "monospace",
            color: "#223142",
            transition: "border-color 0.15s, background-color 0.15s",
            cursor: "text",
            userSelect: "none",
          }}
        >
          {d}
        </Box>
      ))}
      {/* Invisible overlay input captures keystrokes */}
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 6))}
        disabled={disabled}
        maxLength={6}
        inputMode="numeric"
        autoComplete="one-time-code"
        style={{
          position: "absolute",
          inset: 0,
          opacity: 0,
          cursor: "text",
          width: "100%",
          fontSize: 1,
        }}
      />
    </Box>
  );
};

/* ── Main MFA screen ─────────────────────────────────────────────────────── */
const MFAVerify = ({ mfaToken, mfaMethod = "email", emailHint, username, onSuccess, onBack }) => {
  const isTotp = mfaMethod === "totp";
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [code, setCode]               = useState("");
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState("");
  const [resendCount, setResendCount] = useState(0);
  const [cooldown, setCooldown]       = useState(0);   // seconds left
  const [resendOk, setResendOk]       = useState("");  // success notice

  /* Countdown tick */
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const handleVerify = async () => {
    if (code.length !== 6 || loading) return;
    setLoading(true);
    setError("");
    setResendOk("");
    try {
      const res = await api.post("/mfa/verify", { mfaToken, code });
      storeSession(res.data, username);
      onSuccess(res.data);
    } catch (err) {
      const d = err.response?.data || {};
      let msg = d.message || "Incorrect code. Please try again.";
      if (d.attemptsRemaining != null) {
        msg += ` (${d.attemptsRemaining} attempt${d.attemptsRemaining !== 1 ? "s" : ""} remaining)`;
      }
      setError(msg);
      setCode("");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCount >= 3 || cooldown > 0 || loading) return;
    setLoading(true);
    setError("");
    setResendOk("");
    try {
      await api.post("/mfa/resend", { mfaToken });
      setResendCount((n) => n + 1);
      setCooldown(60);
      setResendOk("A new code has been sent to your email.");
      setCode("");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to resend code.");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleVerify();
  };

  const canResend    = resendCount < 3 && cooldown === 0 && !loading;
  const resendsLeft  = 3 - resendCount;

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        width: "100%",
        position: "relative",
        overflow: "hidden",
        backgroundColor: "#eef2f7",
      }}
    >
      {/* Background */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: isMobile ? "url('/hoa_mobile_9x16.jpg')" : "url('/hoa.jpg')",
          backgroundSize: "cover",
          backgroundPosition: isMobile ? "center top" : "center center",
          backgroundRepeat: "no-repeat",
          filter: isMobile ? "blur(4px)" : "blur(6px)",
          transform: "scale(1.05)",
          transformOrigin: "center",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: isMobile
            ? "linear-gradient(180deg, rgba(13,24,43,0.14), rgba(13,24,43,0.34))"
            : "linear-gradient(180deg, rgba(13,24,43,0.10), rgba(13,24,43,0.22))",
          backdropFilter: "blur(3.5px)",
        }}
      />

      {/* Card */}
      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          minHeight: "100dvh",
          display: "flex",
          justifyContent: "center",
          alignItems: { xs: "flex-start", sm: "center" },
          px: { xs: 1.5, sm: 2 },
          pt: { xs: 7, sm: 3 },
          pb: { xs: 4, sm: 3 },
          boxSizing: "border-box",
        }}
      >
        <Fade in timeout={400}>
          <Box
            onKeyDown={handleKeyDown}
            sx={{
              width: "100%",
              maxWidth: { xs: "360px", sm: "430px" },
              px: { xs: 2.25, sm: 4 },
              pt: { xs: 2.5, sm: 3.5 },
              pb: { xs: 3, sm: 4 },
              backgroundColor: "rgba(255,255,255,0.82)",
              borderRadius: { xs: "24px", sm: "20px" },
              boxShadow: {
                xs: "0 14px 40px rgba(0,0,0,0.22)",
                sm: "0 20px 60px rgba(0,0,0,0.30)",
              },
              backdropFilter: "blur(16px)",
              WebkitBackdropFilter: "blur(16px)",
              border: "1px solid rgba(255,255,255,0.38)",
              boxSizing: "border-box",
              textAlign: "center",
            }}
          >
            {/* Shield icon */}
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  backgroundColor: "rgba(47,99,173,0.1)",
                  border: "2px solid rgba(47,99,173,0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ShieldIcon sx={{ fontSize: 30, color: "#2f63ad" }} />
              </Box>
            </Box>

            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "#223142",
                mb: 0.5,
                fontSize: { xs: "1.2rem", sm: "1.55rem" },
                letterSpacing: "-0.02em",
              }}
            >
              Verify Your Identity
            </Typography>

            <Typography
              variant="body2"
              sx={{ color: "#6b778c", mb: 2.5, px: 0.5, fontSize: { xs: "0.88rem", sm: "0.92rem" }, lineHeight: 1.5 }}
            >
              {isTotp ? (
                <>Enter the <Box component="span" sx={{ fontWeight:700, color:"#223142" }}>6-digit code</Box> from your authenticator app (Google Authenticator, Microsoft Authenticator, or similar).</>
              ) : (
                <>Enter the 6-digit code sent to{" "}<Box component="span" sx={{ fontWeight:700, color:"#223142", fontFamily:"monospace" }}>{emailHint || "your email"}</Box>. The code expires in 10 minutes.</>
              )}
            </Typography>

            {/* Error */}
            {error && (
              <Alert
                severity={error.includes("attempts") && error.includes("0 attempt") ? "error" : "warning"}
                sx={{ mb: 1.75, borderRadius: "10px", textAlign: "left", fontSize: "0.85rem" }}
              >
                {error}
              </Alert>
            )}

            {/* Resend success */}
            {resendOk && !error && (
              <Alert severity="success" sx={{ mb: 1.75, borderRadius: "10px", textAlign: "left", fontSize: "0.85rem" }}>
                {resendOk}
              </Alert>
            )}

            {/* OTP input */}
            <Box sx={{ mb: 2.5 }}>
              <OTPInput value={code} onChange={setCode} disabled={loading} />
            </Box>

            {/* Verify button */}
            <Button
              fullWidth
              variant="contained"
              disabled={code.length !== 6 || loading}
              onClick={handleVerify}
              sx={{
                backgroundColor: "#2f63ad",
                color: "white",
                fontWeight: 700,
                fontSize: "1rem",
                py: 1.1,
                borderRadius: "12px",
                textTransform: "none",
                boxShadow: "none",
                mb: 1.5,
                "&:hover": { backgroundColor: "#1f4f94", boxShadow: "none" },
                "&.Mui-disabled": { backgroundColor: "rgba(47,99,173,0.3)", color: "#fff" },
              }}
            >
              {loading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Verify Code"}
            </Button>

            {/* Resend */}
            <Box sx={{ mb: 1.75 }}>
              {isTotp ? (
                <Typography variant="body2" sx={{ color: "#9aa5b4", fontSize: "0.82rem", textAlign:"center" }}>
                  Code refreshes every 30 seconds in your app
                </Typography>
              ) : resendCount >= 3 ? (
                <Typography variant="body2" sx={{ color: "#9aa5b4", fontSize: "0.85rem" }}>
                  Maximum resend limit reached.
                </Typography>
              ) : cooldown > 0 ? (
                <Typography variant="body2" sx={{ color: "#6b778c", fontSize: "0.85rem" }}>
                  Resend available in{" "}
                  <Box component="span" sx={{ fontWeight: 700, color: "#2f63ad", fontFamily: "monospace" }}>
                    {cooldown}s
                  </Box>
                  {resendsLeft < 3 && ` · ${resendsLeft} resend${resendsLeft !== 1 ? "s" : ""} left`}
                </Typography>
              ) : (
                <Button
                  variant="text"
                  size="small"
                  startIcon={<RefreshIcon sx={{ fontSize: "16px !important" }} />}
                  disabled={!canResend}
                  onClick={handleResend}
                  sx={{
                    color: "#2f63ad",
                    fontWeight: 600,
                    textTransform: "none",
                    fontSize: "0.88rem",
                    "&:hover": { backgroundColor: "rgba(47,99,173,0.06)" },
                  }}
                >
                  Resend Code{resendsLeft < 3 ? ` (${resendsLeft} left)` : ""}
                </Button>
              )}
            </Box>

            {/* Back to login */}
            <Link
              href="#"
              underline="hover"
              onClick={(e) => { e.preventDefault(); onBack(); }}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.4,
                color: "#6b778c",
                fontSize: "0.88rem",
                "&:hover": { color: "#223142" },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 15 }} />
              Back to login
            </Link>

            <Typography variant="body2" sx={{ mt: 2.5, color: "#666", fontSize: "0.82rem" }}>
              © My HOA {new Date().getFullYear()}
            </Typography>
          </Box>
        </Fade>
      </Box>
    </Box>
  );
};

export default MFAVerify;
